import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Checkbox, Collapse, Input, Select, Slider, Space, Switch, Table, Tabs, Tag, Typography } from "antd";
import {
  Activity, AlertCircle, Brain, Calendar, CheckCircle, Clock, Info,
  MessageSquare, Mic, Phone, PhoneCall, PhoneForwarded, PhoneIncoming,
  PhoneMissed, RefreshCw, Save, Settings2, Sparkles, Star, TrendingUp,
  User, Voicemail, Wifi, Zap,
} from "lucide-react";

const { Text, Title } = Typography;
const { TextArea } = Input;

type CallOutcome = "booked" | "rescheduled" | "cancelled" | "transferred" | "voicemail" | "no-action";
type CallStatus = "completed" | "missed" | "transferred";

interface CallMessage { id: string; role: "ai" | "customer"; text: string; ms: number; }
interface CallRecord {
  id: string; phone: string; customerName: string | null; isRecognized: boolean;
  startTime: string; duration: string; status: CallStatus; outcome: CallOutcome;
  appointmentAction?: string; transcript: CallMessage[];
  sentiment: "positive" | "neutral" | "negative"; staffTransferReason?: string;
}

const KNOWN_CUSTOMER = { name: "Rachel", lastVisit: "Jul 28", nextAppt: "Aug 12 · 2:00 PM", services: ["Balayage", "Trim"] };

const ACTIVE_CALL_SCRIPT: CallMessage[] = [
  { id: "m1", role: "ai", text: "Thank you for calling Hairplane! This is Aria, your AI assistant. How can I help you today?", ms: 0 },
  { id: "m2", role: "customer", text: "Hi, I need to reschedule my appointment.", ms: 3000 },
  { id: "m3", role: "ai", text: "Of course, Rachel! I can see you have an appointment on August 12th at 2:00 PM for a Balayage and Trim. Would you like to move that to a different date or time?", ms: 2000 },
  { id: "m4", role: "customer", text: "Yes, can I do the 14th instead?", ms: 3000 },
  { id: "m5", role: "ai", text: "Let me check availability on Wednesday August 14th… I have openings at 11:00 AM with Maya, or 3:30 PM with Jordan. Which works better for you?", ms: 2000 },
  { id: "m6", role: "customer", text: "3:30 PM sounds great.", ms: 3000 },
  { id: "m7", role: "ai", text: "Perfect! I've rescheduled your Balayage and Trim to Wednesday, August 14th at 3:30 PM with Jordan Blake. You'll receive a confirmation text shortly. Anything else?", ms: 2000 },
  { id: "m8", role: "customer", text: "No, that's all. Thanks!", ms: 3000 },
  { id: "m9", role: "ai", text: "Wonderful! See you on the 14th, Rachel. Have a great day! 😊", ms: 1500 },
];

const CALL_LOGS: CallRecord[] = [
  { id: "c1", phone: "+1 (212) 555-0192", customerName: "Rachel", isRecognized: true, startTime: "2:38 PM", duration: "1m 48s", status: "completed", outcome: "rescheduled", appointmentAction: "Moved Aug 12 → Aug 14 · 3:30 PM (Jordan)", sentiment: "positive", transcript: [{ id: "t1", role: "ai", text: "Thank you for calling Hairplane! This is Aria. How can I help?", ms: 0 }, { id: "t2", role: "customer", text: "Hi, I need to reschedule my appointment.", ms: 4200 }] },
  { id: "c2", phone: "+1 (212) 555-0147", customerName: "Marcus", isRecognized: true, startTime: "1:52 PM", duration: "2m 14s", status: "completed", outcome: "booked", appointmentAction: "New booking: Aug 16 · 9:30 AM Haircut + Beard Trim (Maya)", sentiment: "positive", transcript: [] },
  { id: "c3", phone: "+1 (917) 555-0083", customerName: null, isRecognized: false, startTime: "1:21 PM", duration: "3m 05s", status: "transferred", outcome: "transferred", staffTransferReason: "New client — requested pricing details", sentiment: "neutral", transcript: [] },
  { id: "c4", phone: "+1 (212) 555-0308", customerName: "Priya", isRecognized: true, startTime: "12:44 PM", duration: "1m 02s", status: "completed", outcome: "booked", appointmentAction: "New booking: Aug 19 · 2:00 PM Color Treatment (Taylor)", sentiment: "positive", transcript: [] },
  { id: "c5", phone: "+1 (718) 555-0214", customerName: null, isRecognized: false, startTime: "11:58 AM", duration: "0m 00s", status: "missed", outcome: "voicemail", sentiment: "neutral", transcript: [] },
  { id: "c6", phone: "+1 (212) 555-0451", customerName: "James", isRecognized: true, startTime: "11:33 AM", duration: "0m 55s", status: "completed", outcome: "cancelled", appointmentAction: "Cancelled: Aug 9 · 10:00 AM Haircut", sentiment: "neutral", transcript: [] },
  { id: "c7", phone: "+1 (212) 555-0629", customerName: "Sofia", isRecognized: true, startTime: "10:15 AM", duration: "1m 38s", status: "completed", outcome: "booked", appointmentAction: "New booking: Aug 22 · 1:00 PM Highlights + Cut (Priya)", sentiment: "positive", transcript: [] },
  { id: "c8", phone: "+1 (929) 555-0177", customerName: null, isRecognized: false, startTime: "9:42 AM", duration: "4m 12s", status: "transferred", outcome: "transferred", staffTransferReason: "Complaint about previous service", sentiment: "negative", transcript: [] },
];

const OUTCOME_META: Record<CallOutcome, { label: string; color: string; icon: React.ReactNode }> = {
  booked: { label: "Booked", color: "success", icon: <CheckCircle size={11} /> },
  rescheduled: { label: "Rescheduled", color: "blue", icon: <RefreshCw size={11} /> },
  cancelled: { label: "Cancelled", color: "orange", icon: <PhoneMissed size={11} /> },
  transferred: { label: "Transferred", color: "gold", icon: <PhoneForwarded size={11} /> },
  voicemail: { label: "Voicemail", color: "default", icon: <Voicemail size={11} /> },
  "no-action": { label: "No action", color: "default", icon: <Phone size={11} /> },
};

const HEATMAP_HOURS = ["9 AM", "10 AM", "11 AM", "12 PM", "1 PM", "2 PM", "3 PM", "4 PM", "5 PM"];
const HEATMAP_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function heatValue(hourIdx: number, dayIdx: number) {
  // deterministic pseudo-demand pattern, peaks midday/late-week
  return Math.max(1, Math.round(10 - Math.abs(hourIdx - 5) * 1.3 + dayIdx * 0.6));
}
function heatColor(v: number) {
  if (v <= 2) return "#f3f4f6";
  if (v <= 4) return "#dbeafe";
  if (v <= 6) return "#c7d2fe";
  if (v <= 8) return "#c4b5fd";
  return "#8b5cf6";
}

function ActiveCallCard() {
  const [visible, setVisible] = useState<CallMessage[]>([]);
  const [seconds, setSeconds] = useState(0);
  const [phase, setPhase] = useState<"ringing" | "active" | "completed">("ringing");
  const [idx, setIdx] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setPhase("active"), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== "active") return;
    if (idx >= ACTIVE_CALL_SCRIPT.length) {
      const t = setTimeout(() => setPhase("completed"), 1200);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setVisible((m) => [...m, ACTIVE_CALL_SCRIPT[idx]]);
      setIdx((i) => i + 1);
    }, ACTIVE_CALL_SCRIPT[idx].ms || 300);
    return () => clearTimeout(t);
  }, [idx, phase]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [visible]);

  useEffect(() => {
    if (phase !== "completed") return;
    const t = setTimeout(() => { setPhase("ringing"); setVisible([]); setIdx(0); setSeconds(0); }, 4000);
    return () => clearTimeout(t);
  }, [phase]);

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const headerColor = phase === "completed" ? "#10b981" : phase === "active" ? "#4f46e5" : "#f59e0b";

  return (
    <Card size="small" style={{ borderTop: `3px solid ${headerColor}`, overflow: "hidden" }} bodyStyle={{ padding: 0 }}>
      <div style={{ background: headerColor, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, color: "#fff" }}>
        {phase === "ringing" ? <PhoneIncoming size={16} /> : phase === "active" ? <PhoneCall size={16} /> : <CheckCircle size={16} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{phase === "ringing" ? "Incoming call…" : phase === "active" ? "Live AI Call" : "Call Completed"}</div>
          <div style={{ fontSize: 11, opacity: 0.8 }}>+1 (212) 555-0192</div>
        </div>
        <Tag style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff" }}><Zap size={10} style={{ marginRight: 4 }} />Recognized: {KNOWN_CUSTOMER.name}</Tag>
        <span style={{ fontVariantNumeric: "tabular-nums", fontFamily: "monospace" }}>{fmt(seconds)}</span>
      </div>

      <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
        <div>
          <div style={{ background: "#fafafa", borderRadius: 10, padding: 12 }}>
            <Space>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#4f46e5", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{KNOWN_CUSTOMER.name.charAt(0)}</div>
              <div><Text strong style={{ fontSize: 13 }}>{KNOWN_CUSTOMER.name}</Text><div style={{ fontSize: 11, color: "#9ca3af" }}>+1 (212) 555-0192</div></div>
            </Space>
            <div style={{ marginTop: 10, fontSize: 11 }}>
              <div style={{ color: "#595959" }}><Clock size={10} style={{ marginRight: 4 }} />Last visit: {KNOWN_CUSTOMER.lastVisit}</div>
              <div style={{ color: "#4f46e5", fontWeight: 600, marginTop: 3 }}><Calendar size={10} style={{ marginRight: 4 }} />Next: {KNOWN_CUSTOMER.nextAppt}</div>
              <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                {KNOWN_CUSTOMER.services.map((s) => <Tag key={s} color="purple" style={{ fontSize: 10 }}>{s}</Tag>)}
              </div>
            </div>
          </div>
          {phase === "active" && visible.length >= 3 && (
            <Alert style={{ marginTop: 10, fontSize: 12 }} type="info" showIcon icon={<Brain size={13} />} message="AI Intent Detected" description="Reschedule existing appointment → offering Aug 14 slots" />
          )}
          {phase === "completed" && (
            <Alert style={{ marginTop: 10, fontSize: 12 }} type="success" showIcon message="Call Resolved" description="Rescheduled: Aug 12 → Aug 14 · 3:30 PM with Jordan Blake" />
          )}
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontSize: 11, color: "#8c8c8c", textTransform: "uppercase", fontWeight: 600 }}>
            <MessageSquare size={12} />Live Transcript {phase === "active" && <Tag color="success" style={{ fontSize: 10 }}>● Live</Tag>}
          </div>
          <div ref={scrollRef} style={{ height: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            {phase === "ringing" ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#bfbfbf", gap: 8 }}><PhoneIncoming size={16} />Connecting…</div>
            ) : visible.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}><RefreshCw size={11} style={{ marginRight: 4 }} />AI answering…</Text>
            ) : (
              visible.map((m) => (
                <div key={m.id} style={{ display: "flex", justifyContent: m.role === "ai" ? "flex-start" : "flex-end" }}>
                  <div style={{ maxWidth: "80%", padding: "6px 10px", borderRadius: 12, fontSize: 12, background: m.role === "ai" ? "#eef2ff" : "#f5f5f5", color: m.role === "ai" ? "#3730a3" : "#262626" }}>
                    {m.text}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function MonitorTab() {
  const total = CALL_LOGS.length;
  const aiHandled = CALL_LOGS.filter((c) => c.outcome !== "transferred").length;
  const booked = CALL_LOGS.filter((c) => c.outcome === "booked").length;
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div>
      <ActiveCallCard />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, margin: "16px 0" }}>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Calls Today</Text><div style={{ fontSize: 20, fontWeight: 700, color: "#4f46e5" }}>{total}</div><Text type="secondary" style={{ fontSize: 11 }}>+2 vs yesterday</Text></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>AI Resolution Rate</Text><div style={{ fontSize: 20, fontWeight: 700, color: "#059669" }}>{Math.round((aiHandled / total) * 100)}%</div><Text type="secondary" style={{ fontSize: 11 }}>{aiHandled} of {total} handled</Text></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Bookings Created</Text><div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb" }}>{booked}</div><Text type="secondary" style={{ fontSize: 11 }}>via AI this session</Text></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Avg Call Duration</Text><div style={{ fontSize: 20, fontWeight: 700, color: "#7c3aed" }}>1m 38s</div><Text type="secondary" style={{ fontSize: 11 }}>AI-handled calls</Text></Card>
      </div>

      <Card size="small" title={<span><Activity size={13} style={{ marginRight: 6 }} />Today's Call Activity</span>}>
        {CALL_LOGS.map((call, i) => {
          const oc = OUTCOME_META[call.outcome];
          const open = expanded === call.id;
          return (
            <div key={call.id} style={{ borderTop: i > 0 ? "1px solid #f0f0f0" : undefined }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", cursor: "pointer" }} onClick={() => setExpanded(open ? null : call.id)}>
                {call.status === "completed" ? <Phone size={14} color="#10b981" /> : call.status === "missed" ? <PhoneMissed size={14} color="#ef4444" /> : <PhoneForwarded size={14} color="#f59e0b" />}
                <div style={{ flex: 1 }}>
                  <Space size={6}>
                    <Text strong style={{ fontSize: 13 }}>{call.customerName ?? call.phone}</Text>
                    {call.isRecognized && <Tag color="purple" style={{ fontSize: 10 }}><Zap size={8} style={{ marginRight: 2 }} />Known</Tag>}
                  </Space>
                  {(call.appointmentAction || call.staffTransferReason) && (
                    <div style={{ fontSize: 11, color: call.staffTransferReason ? "#d97706" : "#9ca3af" }}>{call.appointmentAction ?? call.staffTransferReason}</div>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>{call.startTime}</Text>
                <Tag color={oc.color} icon={oc.icon}>{oc.label}</Tag>
              </div>
              {open && call.transcript.length > 0 && (
                <div style={{ paddingBottom: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  {call.transcript.map((m) => (
                    <div key={m.id} style={{ display: "flex", justifyContent: m.role === "ai" ? "flex-start" : "flex-end" }}>
                      <div style={{ maxWidth: "85%", padding: "5px 10px", borderRadius: 10, fontSize: 12, background: m.role === "ai" ? "#eef2ff" : "#f5f5f5" }}>{m.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function LogsTab() {
  const [filter, setFilter] = useState<CallOutcome | "all">("all");
  const visible = filter === "all" ? CALL_LOGS : CALL_LOGS.filter((c) => c.outcome === filter);
  const sentimentIcon = (s: CallRecord["sentiment"]) =>
    s === "positive" ? <Star size={11} color="#fbbf24" fill="#fbbf24" /> : s === "negative" ? <AlertCircle size={11} color="#f87171" /> : <Star size={11} color="#d9d9d9" />;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {(["all", "booked", "rescheduled", "cancelled", "transferred", "voicemail"] as const).map((f) => (
          <Button key={f} size="small" type={filter === f ? "primary" : "default"} onClick={() => setFilter(f)} style={{ textTransform: "capitalize" }}>
            {f === "all" ? `All (${CALL_LOGS.length})` : `${f} (${CALL_LOGS.filter((c) => c.outcome === f).length})`}
          </Button>
        ))}
      </div>
      <Table
        rowKey="id"
        size="small"
        dataSource={visible}
        pagination={false}
        columns={[
          {
            title: "Caller", dataIndex: "customerName",
            render: (v: string | null, c: CallRecord) => (
              <Space>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#eef2ff", color: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>{v?.charAt(0) ?? "#"}</div>
                <div><div style={{ fontWeight: 600, fontSize: 13 }}>{v ?? "Unknown"}</div><div style={{ fontSize: 10, color: "#9ca3af" }}>{c.phone}</div></div>
                {c.isRecognized && <Tag color="purple" style={{ fontSize: 9 }}>Known</Tag>}
              </Space>
            ),
          },
          { title: "Time", dataIndex: "startTime", width: 80 },
          { title: "Duration", dataIndex: "duration", width: 80 },
          { title: "Outcome", dataIndex: "outcome", render: (v: CallOutcome) => <Tag color={OUTCOME_META[v].color} icon={OUTCOME_META[v].icon}>{OUTCOME_META[v].label}</Tag> },
          { title: "Action Taken", key: "action", render: (_: unknown, c: CallRecord) => <span style={{ fontSize: 12 }}>{c.appointmentAction ?? c.staffTransferReason ?? "—"}</span> },
          { title: "Sentiment", dataIndex: "sentiment", render: (v: CallRecord["sentiment"]) => <Space size={4}>{sentimentIcon(v)}<span style={{ fontSize: 12, textTransform: "capitalize" }}>{v}</span></Space> },
        ]}
      />
    </div>
  );
}

function SchedulingTab() {
  const insights = [
    { icon: <TrendingUp size={13} />, title: "Peak demand window", body: "Tuesday–Friday 2–4 PM has the highest booking rate. Consider adding a 4th stylist on these shifts." },
    { icon: <Calendar size={13} />, title: "Under-utilized slots", body: "Monday mornings (9–11 AM) show 3× lower demand. Ideal for staff training or walk-in promotions." },
    { icon: <Phone size={13} />, title: "Repeat reschedule pattern", body: "28% of callers reschedule to Friday/Saturday. Pre-blocking 2 flexible slots per day reduces conflicts." },
  ];
  const actions = [
    { done: true, label: "Block 2 flexible slots Mon/Wed 9–10 AM for walk-in overflow", sub: "Applied this week" },
    { done: true, label: "Extend AI handling window to 8 PM on Fri/Sat", sub: "Active since Aug 5" },
    { done: false, label: "Add 4th stylist to Tue–Fri 2–4 PM high-demand window", sub: "Pending staff confirmation" },
    { done: false, label: 'Send proactive "fill gap" SMS for Monday 9 AM openings', sub: "Scheduled for Mon 7 AM" },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card size="small" style={{ background: "linear-gradient(135deg,#eef2ff,#f5f3ff)" }}>
        <Space><Sparkles size={16} color="#4f46e5" /><Text strong>AI Scheduling Insights</Text></Space>
        <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 12 }}>Based on this week's call patterns and calendar data</Text>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {insights.map((t) => (
            <Card key={t.title} size="small">
              <Space size={6} style={{ color: "#4f46e5", fontWeight: 600, fontSize: 12 }}>{t.icon}{t.title}</Space>
              <div style={{ fontSize: 12, color: "#595959", marginTop: 6 }}>{t.body}</div>
            </Card>
          ))}
        </div>
      </Card>

      <Card size="small" title={<span>Booking Demand Heatmap</span>} extra={<Text type="secondary" style={{ fontSize: 11 }}>Last 2 weeks</Text>}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderSpacing: 3, borderCollapse: "separate", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 50 }} />
                {HEATMAP_DAYS.map((d) => <th key={d} style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 600 }}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {HEATMAP_HOURS.map((hour, hi) => (
                <tr key={hour}>
                  <td style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>{hour}</td>
                  {HEATMAP_DAYS.map((d, di) => {
                    const v = heatValue(hi, di);
                    return <td key={d} style={{ background: heatColor(v), borderRadius: 6, textAlign: "center", padding: "6px 4px", fontSize: 11, color: v > 8 ? "#fff" : "#374151" }}>{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <Space style={{ marginTop: 12, fontSize: 11, color: "#8c8c8c" }} size={10}>
            <span>Demand:</span>
            {[["#f3f4f6", "Low"], ["#dbeafe", "3–4"], ["#c7d2fe", "5–6"], ["#c4b5fd", "7–8"], ["#8b5cf6", "Peak"]].map(([c, l]) => (
              <span key={l}><span style={{ display: "inline-block", width: 10, height: 10, background: c, borderRadius: 3, marginRight: 4 }} />{l}</span>
            ))}
          </Space>
        </div>
      </Card>

      <Card size="small" title={<span><Zap size={13} color="#4f46e5" style={{ marginRight: 6 }} />Auto-Optimization Actions</span>}>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {actions.map((a) => (
            <div key={a.label} style={{ display: "flex", gap: 10, padding: 10, background: "#fafafa", borderRadius: 10 }}>
              {a.done ? <CheckCircle size={15} color="#10b981" /> : <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #a5b4fc", marginTop: 1 }} />}
              <div><div style={{ fontSize: 13 }}>{a.label}</div><div style={{ fontSize: 11, color: "#9ca3af" }}>{a.sub}</div></div>
            </div>
          ))}
        </Space>
      </Card>
    </Space>
  );
}

function ConfigTab() {
  const [greeting, setGreeting] = useState("Thank you for calling {salon_name}! This is Aria, your AI assistant. How can I help you today?");
  const [voice, setVoice] = useState("aria-f");
  const [aiEnabled, setAiEnabled] = useState(true);

  const voices = [
    { id: "aria-f", name: "Aria", gender: "Female", tone: "Warm · Professional" },
    { id: "nova-f", name: "Nova", gender: "Female", tone: "Friendly · Upbeat" },
    { id: "echo-m", name: "Echo", gender: "Male", tone: "Calm · Confident" },
    { id: "onyx-m", name: "Onyx", gender: "Male", tone: "Deep · Authoritative" },
  ];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center" }}><Brain size={16} color="#fff" /></div>
          <div style={{ flex: 1 }}><Text strong>AI Telephony Concierge</Text><div style={{ fontSize: 11, color: "#9ca3af" }}>All inbound calls are handled by Aria · transfer rules apply</div></div>
          <Switch checked={aiEnabled} onChange={setAiEnabled} />
        </div>
      </Card>

      <Collapse
        defaultActiveKey={["phone"]}
        items={[
          {
            key: "phone",
            label: <span><Phone size={13} style={{ marginRight: 6 }} />Phone Number & Routing</span>,
            children: (
              <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <Card size="small" style={{ background: "#eef2ff" }}>
                  <Space><Phone size={16} color="#4f46e5" /><div><Text strong>+1 (212) 555-0100</Text><div style={{ fontSize: 11, color: "#4f46e5" }}>AI Concierge line · Active</div></div><Tag color="success" style={{ marginLeft: "auto" }}><Wifi size={10} style={{ marginRight: 3 }} />Live</Tag></Space>
                </Card>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div><Text type="secondary" style={{ fontSize: 11 }}>Open hours</Text><Select style={{ width: "100%", marginTop: 4 }} defaultValue="a" options={[{ value: "a", label: "8:00 AM – 9:00 PM (daily)" }, { value: "b", label: "9:00 AM – 8:00 PM (daily)" }]} /></div>
                  <div><Text type="secondary" style={{ fontSize: 11 }}>After-hours behavior</Text><Select style={{ width: "100%", marginTop: 4 }} defaultValue="a" options={[{ value: "a", label: "Take voicemail + SMS follow-up" }, { value: "b", label: "Offer next available slot" }, { value: "c", label: "Forward to on-call staff" }]} /></div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><Text style={{ fontSize: 13 }}>Handle after-hours calls with AI</Text><Switch defaultChecked /></div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>Transfer to staff when</Text>
                  <Space direction="vertical" size={4}>
                    {["Customer requests human", "Complaint detected", "New client pricing query", "Medical/allergy concern"].map((r) => <Checkbox key={r} defaultChecked>{r}</Checkbox>)}
                  </Space>
                </div>
              </Space>
            ),
          },
          {
            key: "voice",
            label: <span><Mic size={13} style={{ marginRight: 6 }} />AI Voice & Personality</span>,
            children: (
              <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {voices.map((v) => (
                    <Card key={v.id} size="small" hoverable onClick={() => setVoice(v.id)} style={{ borderColor: voice === v.id ? "#818cf8" : undefined, background: voice === v.id ? "#eef2ff" : undefined, cursor: "pointer" }}>
                      <Space>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: voice === v.id ? "#4f46e5" : "#9ca3af", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{v.name.charAt(0)}</div>
                        <div><div style={{ fontSize: 12, fontWeight: 600 }}>{v.name} · {v.gender}</div><div style={{ fontSize: 10, color: "#9ca3af" }}>{v.tone}</div></div>
                      </Space>
                    </Card>
                  ))}
                </div>
                <div><Text type="secondary" style={{ fontSize: 11 }}>AI name (spoken)</Text><Input defaultValue="Aria" style={{ marginTop: 4 }} /></div>
                <div><Text type="secondary" style={{ fontSize: 11 }}>Language & locale</Text><Select style={{ width: "100%", marginTop: 4 }} defaultValue="en-us" options={[{ value: "en-us", label: "English (US)" }, { value: "en-uk", label: "English (UK)" }, { value: "es-us", label: "Spanish (US)" }, { value: "fr-ca", label: "French (CA)" }]} /></div>
                <div><Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>Speaking speed</Text><Slider min={0.75} max={1.25} step={0.05} defaultValue={1} /></div>
              </Space>
            ),
          },
          {
            key: "greeting",
            label: <span><MessageSquare size={13} style={{ marginRight: 6 }} />Greeting Script & Prompts</span>,
            children: (
              <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Opening greeting · variables: {"{salon_name} {customer_name} {time_of_day}"}</Text>
                  <TextArea value={greeting} onChange={(e) => setGreeting(e.target.value)} rows={3} style={{ marginTop: 4 }} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Recognized customer greeting</Text>
                  <TextArea defaultValue="Welcome back, {customer_name}! Great to hear from you. I can see your next appointment is on {next_appt}. How can I help?" rows={2} style={{ marginTop: 4 }} />
                </div>
                <Alert type="info" showIcon icon={<Info size={13} />} message="Customer name is auto-injected when the caller's phone number matches a client record. New callers hear the default greeting." />
              </Space>
            ),
          },
          {
            key: "recognition",
            label: <span><User size={13} style={{ marginRight: 6 }} />Customer Recognition & Privacy</span>,
            children: (
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                {[
                  { label: "Auto-identify callers by phone number", on: true },
                  { label: "Greet returning clients by first name", on: true },
                  { label: "Read upcoming appointments aloud", on: true },
                  { label: "Offer past service as rebooking default", on: true },
                  { label: "Record & transcribe calls", on: true },
                  { label: "Share transcripts with staff", on: false },
                ].map((item) => (
                  <div key={item.label} style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text style={{ fontSize: 13 }}>{item.label}</Text>
                    <Switch defaultChecked={item.on} />
                  </div>
                ))}
                <Alert type="warning" showIcon icon={<AlertCircle size={13} />} message="Call recording and transcription complies with local regulations. Callers are notified at the start of each call." />
              </Space>
            ),
          },
        ]}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <Button type="primary" icon={<Save size={13} />}>Save Configuration</Button>
      </div>
    </div>
  );
}

export default function AdminAITelephonyPage() {
  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center" }}><Brain size={16} color="#fff" /></div>
            <Title level={3} style={{ margin: 0 }}>AI Telephony Concierge</Title>
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>Intelligent auto-attendant · real-time scheduling · customer recognition</Text>
        </div>
        <Space>
          <Tag color="success">● Aria · Active</Tag>
          <Tag><Phone size={10} style={{ marginRight: 4 }} />+1 (212) 555-0100</Tag>
        </Space>
      </div>

      <Alert
        style={{ marginBottom: 16 }}
        type="warning"
        showIcon
        message="Demo preview — not a live phone line"
        description="Fully interactive so you can try the flow (including a simulated live call), but no real calls are being answered. Needs a real voice/telephony provider plus an LLM voice pipeline to go live."
      />

      <Tabs
        items={[
          { key: "monitor", label: <span><Activity size={13} style={{ marginRight: 5 }} />Live Monitor</span>, children: <MonitorTab /> },
          { key: "logs", label: <span><PhoneCall size={13} style={{ marginRight: 5 }} />Call Logs</span>, children: <LogsTab /> },
          { key: "scheduling", label: <span><Brain size={13} style={{ marginRight: 5 }} />Smart Scheduling</span>, children: <SchedulingTab /> },
          { key: "config", label: <span><Settings2 size={13} style={{ marginRight: 5 }} />Configuration</span>, children: <ConfigTab /> },
        ]}
      />
    </div>
  );
}
